import React, { useEffect, useRef, useState } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { Outlet, Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { NavigationBar } from '../Components/navigationbar/NavigationBar';
import { Footer } from '../Components/Layout/footer/Footer';
import Layout from '../Components/Layout';
import styles from './landingPage.module.scss';
import MainButton from '../Components/common/buttons/MainButton';
import { buttonVariants } from '../Components/common/buttons/types';
import { contentSectionOne, contentSectionTwo, faqContent } from './landingPages/content';
import SectionCard from '../Components/common/Cards/SectionCard/SectionCard';
import { Faq } from '../Components/Faq/Faq';
import { FaqTypes, responsiveSizeJobs } from '../utils/constants';
import GlobalSearchandLocation from '../Components/molecules/GlobalSearchandLocation/GlobalSearchandLocation';
import { useDebounce, useWindowSize } from '../utils/hooks';
import { fetchSmartLocations } from '../actions/locations';
import { getSearchSuggestion } from '../actions/jobs';
import { loginRequest } from '../authConfig';
import colors from '../scss/colors';
import { CircularProgress } from '@mui/material';
import { logAppError, logAuthStep } from '../utils/logActions';
// import { useConvert } from '../context/ConvertProvider';

export function LandingPage() {
  // In Case if convert.com variant need to be used in the future
  // const { convertContext, isReady } = useConvert();
  const { width } = useWindowSize();
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [faqData, setFaqData] = useState(faqContent(t).accounts);
  const [placeholderMobile, setPlaceholderMobile] = useState(false);
  const locationRef = useRef(null);
  const searchRef = useRef(null);
  const [searchFocused, setSearchFocused] = useState(width > responsiveSizeJobs);
  const [clickedLogin, setClickedLogin] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const sectionRef = useRef(null);
  const placeholderRef = useRef(null);
  const heroRef = useRef(null);
  const [filters, setFilters] = useState({
    searchTxt: '',
    city: '',
    country: '',
    state: '',
    location: '',
    suggestionTriggered: false
  });

  useEffect(() => {
    setFaqData(faqContent(t).accounts);
  }, [t]);

  const handleOnSelectFaq = (type) => {
    switch (type) {
      case FaqTypes.ACCOUNTS:
        setFaqData(faqContent(t).accounts);
        break;
      case FaqTypes.JOBS:
        setFaqData(faqContent(t).jobs);
        break;
      case FaqTypes.OTHERS:
        setFaqData(faqContent(t).others);
        break;
      default:
        setFaqData(faqContent(t).accounts);
    }
  };

  const fetchLocation = useDebounce((e) => {
    dispatch(fetchSmartLocations(e.target.value));
  }, 500);

  const fetchSearchSuggestions = useDebounce((e) => {
    dispatch(getSearchSuggestion(e.target.value));
  }, 500);

  const searchHandler = () => {
    setPlaceholderMobile(false);
    navigate({
      pathname: '/jobs',
      search: `&searchTxt=${filters.searchTxt}` +
              `&city=${filters.city}` +
              `&country=${filters.country}` +
              `&state=${filters.state}` +
              `&location=${filters.location}`
    });
  };


  const searchInputHandler = (newValue) => {
    setFilters({ ...filters, searchTxt: newValue });
    if (newValue.length > 0) locationRef.current.focus();
  };

  const locationHandler = (newValue) => {
    if (newValue?.length) {
      const [city, state, country] = newValue.trim().split(',');
      setFilters({ ...filters, city, state, country, location: '' });
      if (width < responsiveSizeJobs) setSearchFocused(false);
    }
  };

  const onInputHandler = (e) => {
    setFilters({ ...filters, city: '', state: '', country: '', location: e.target.value });
    if (e.target.value.trim().length > 2) {
      fetchLocation(e);
    }
  };

  const onKeyDownSearch = (e) => {
    setFilters({ ...filters, searchTxt: e.target.value });
    if (e.target.value.trim().length > 2) {
      fetchSearchSuggestions(e);
    }
  };

  const clearSearchHandler = () => {
    setFilters({ ...filters, searchTxt: '' });
  };

  const clearLocationHandler = () => {
    setFilters({ ...filters, city: '', state: '', country: '', location: '' });
  };

  const suggestionHandler = (newValue) => {
    const suggestion = newValue.trim().split('Jobs in');
    const isLocationSplit = suggestion[1].includes(',');
    const [city, state, country] = suggestion[1].trim().split(',');
    setFilters({
      ...filters,
      searchTxt: suggestion[0].trim(),
      location: !isLocationSplit ? suggestion[1].trim() : '',
      city: isLocationSplit ? city?.trim() : '',
      state: isLocationSplit ? state?.trim() : '',
      country: isLocationSplit ? country?.trim() : '',
      suggestionTriggered: true
    });
  };

  const locationValue = () => {
    if (filters?.city && filters.state && filters.country) {
      return `${filters?.city}, ${filters?.state}, ${filters?.country}`;
    }

    return '';
  };

  const onFocusHandler = () => {
    setSearchFocused(true);
  };

  useEffect(() => {
    const handleScroll = () => {
      if (sectionRef.current && placeholderRef.current) {
        const sectionTop = placeholderRef.current.getBoundingClientRect().top;

        if (sectionTop <= 0) {
          setIsSticky(true);
        } else {
          setIsSticky(false);
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const Login = () => {
    setClickedLogin(true)
    return instance.loginRedirect({ ...loginRequest, redirectUri: `${window.location.origin}/dashboard` })
      .then(() => {
        logAuthStep('Login_Initiated_After_Click');
        setClickedLogin(false);
      }).catch((e) => {
        logAppError(e, 'Login_Init');
        console.error(e);
      });
  }

  if (isAuthenticated) {
    return (
      <Layout>
        {inProgress === 'none' && !window.location.pathname.includes('/jobs') ? (
          <Navigate to="/dashboard" />
        ) : (
          <Outlet />
        )}
      </Layout>
    );
  }

  return (
    <div className={`${styles.landingPage} flex flex-col sm:gap-0 gap-4`}>
      <div className="flex flex-row justify-center">
        <div className="flex flex-col p-0">
          <div className={styles.backgroundWrapper} ref={heroRef}>
            <div className="flex justify-center w-full">
              <NavigationBar isDark={true} />
            </div>
            <div className="flex justify-center text-center">
              <h1>{t('landingPages.hero.title')}</h1>
            </div>
            <div className="flex justify-center text-center">
              <h2>{t('landingPages.hero.subtitle')}</h2>
            </div>
            <div className="flex justify-center text-center">
              <p className={styles.description}>
                {t('landingPages.hero.description')}
              </p>
            </div>
            <div className="flex justify-center text-center">
              <MainButton
                variant={buttonVariants.LIGHT_BLUE}
                rounded={true}
                size="xmedium"
                action={() => Login()}
                customClass={styles.loginButton}
                sx={{
                  fontWeight: '700',
                  textShadow: '0 1px 2px rgba(0, 0, 0, 0.4)'
                }}
              >
                {clickedLogin ? (
                  <CircularProgress size="1rem" sx={{ color: colors.WHITE }} />
                ) : (
                  t('landingPages.hero.cta')
                )}
              </MainButton>
            </div>
            <div className={placeholderMobile ? styles.searchWrapperFocused : styles.searchWrapper}>
              <div className={styles.searchBar}>
                <GlobalSearchandLocation
                  searchHandler={searchHandler}
                  setSearchValue={searchInputHandler}
                  searchValue={filters.searchTxt}
                  locationValue={filters.location || locationValue()}
                  setLocationValue={locationHandler}
                  onKeyDownHandler={onInputHandler}
                  onKeyDownSearch={onKeyDownSearch}
                  clearLocationHandler={clearLocationHandler}
                  clearSearchHandler={clearSearchHandler}
                  suggestionHandler={suggestionHandler}
                  onFocusHandler={onFocusHandler}
                  isSearchFocused={searchFocused}
                  placeholderMobileHandler={setPlaceholderMobile}
                  placeholderMobile={placeholderMobile}
                  locationRef={locationRef}
                  searchRef={searchRef}
                  searchButtonVariant={buttonVariants.PRIMARY}
                  suggestions
                />
                {width < responsiveSizeJobs && placeholderMobile && (
                  <div className={styles.containerButtons}>
                    <MainButton
                      size="xsmall"
                      variant={buttonVariants.LIGHT_BLUE}
                      customRadius="10px 10px 10px 10px"
                      action={searchHandler}
                      forceWidth="100%"
                      sx={{ marginTop: '10px' }}
                    >
                      {t('dashboard.jobs.search')}
                    </MainButton>
                  </div>
                )}
              </div>
            </div>
            {placeholderMobile && (
              <div
                role="presentation"
                className={styles.backdrop}
                onClick={() => setPlaceholderMobile(false)}
              />
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-col sm:gap-0 gap-4">
        {contentSectionOne(t).map((section) => (
          <div className="flex w-full justify-center" key={section.id}>
            <div className={styles.sectionCard}>
              <SectionCard
                key={section.id}
                imagePosition={section.imagePosition}
                imageBackdrop={section.imageBackdrop}
                image={section.image}
                title={section.title}
                content={section.content}
                cardColor={section.cardColor}
                isDark={section.isDark}
              />
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-row pt-6 w-full">
        <div className="flex flex-col w-full">
          <div ref={placeholderRef}></div>
          <div ref={sectionRef} className={isSticky ? styles.stickyCTA : styles.flyingCTA}>
            <span>{t('landingPages.hero.ctaTitle')}</span>
            <MainButton
              variant={buttonVariants.LIGHT_BLUE}
              rounded={true}
              size="small"
              action={() => Login()}
              sx={{
                fontWeight: 700,
                textShadow: '0 1px 2px rgba(0, 0, 0, 0.4)'
              }}
            >
              {clickedLogin ? (
                <CircularProgress size="1rem" sx={{ color: colors.WHITE }} />
              ) : (
                t('landingPages.hero.cta')
              )}
            </MainButton>
          </div>
        </div>
      </div>
      {/* <div className="flex flex-col pt-5 px-0">
        <div ref={placeholderRef}></div>
        <div ref={sectionRef} className={isSticky ? styles.stickyCTA : styles.flyingCTA}>
          <span>{t('landingPages.hero.ctaTitle')}</span>
          <MainButton
            variant={buttonVariants.LIGHT_BLUE}
            rounded={true}
            size="small"
            action={() => Login()}
          >
            {clickedLogin ? (
              <CircularProgress size="1rem" sx={{ color: colors.WHITE }} />
            ) : (
              t('landingPages.hero.cta')
            )}
          </MainButton>
        </div>
      </div> */}

      <div className="flex flex-col">
        {contentSectionTwo(t).map((section) => (
          <div className="flex justify-center" key={section.id}>
            <div className={styles.sectionCard}>
              <SectionCard
                key={section.id}
                imagePosition={section.imagePosition}
                imageBackdrop={section.imageBackdrop}
                image={section.image}
                title={section.title}
                content={section.content}
                cardColor={section.cardColor}
                isDark={section.isDark}
              />
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-col py-5">
        <div className="flex justify-center">
          <div className={styles.faqWrapper}>
            <Faq onSelect={(type) => handleOnSelectFaq(type)} selectedData={faqData} />
          </div>
        </div>
      </div>

      <div className="mt-auto p-0">
        <Footer />
      </div>
    </div>

  );
}
